<template>
	<form class="main-wrapper mt-4 mb-3" @submit.prevent="handleSubmit">
		<BannerGeneral
			:id="id"
			ref="general"
			name-label="Highlight LOB banner name*"
			:name="name"
			:priority="priority"
			:status="status"
			:widget-type="widgetType"
			show-page-widget
			:page-widgets="pageWidgets"
			@update:name="name = $event"
			@update:priority="priority = $event"
			@update:status="status = $event"
			@update:pageWidgets="pageWidgets = $event"
		/>

		<BannerDate
			ref="date"
			:status="status"
			:start-date="startDate"
			:start-time="startTime"
			:end-date="endDate"
			:end-time="endTime"
			@update:startDate="startDate = $event"
			@update:startTime="startTime = $event"
			@update:endDate="endDate = $event"
			@update:endTime="endTime = $event"
		/>

		<BannerContentWrapper
			ref="contentLob"
			:banner-link="bannerLink"
			desktop-img="/assets/images/highlight-left-banner-desktop.svg"
			mobile-img="/assets/images/highlight-banner-mobile.svg"
			required-link
			has-title
			required-title
			:title-th="bannerTitleTh"
			:title-en="bannerTitleEn"
			has-description
			is-required-description
			:description-th="bannerDescriptionTh"
			:description-en="bannerDescriptionEn"
			has-button-text
			is-required-button-text
			:button-text-th="bannerButtonTextTh"
			:button-text-en="bannerButtonTextEn"
			has-display-price
			is-required-display-price
			:display-price="bannerLowestPrice"
			has-badge-label
			:badge-labels="bannerLabels"
			@update:badgeLabels="bannerLabels = $event"
			@update:bannerLink="bannerLink = $event"
			@update:titleTh="bannerTitleTh = $event"
			@update:titleEn="bannerTitleEn = $event"
			@update:descriptionTh="bannerDescriptionTh = $event"
			@update:descriptionEn="bannerDescriptionEn = $event"
			@update:buttonTextTh="bannerButtonTextTh = $event"
			@update:buttonTextEn="bannerButtonTextEn = $event"
			@update:displayPrice="bannerLowestPrice = $event"
		>
			<template #title>
				Banner image
			</template>
			<BaseMediaSelectorBox
				ref="bannerDesktop"
				v-model="bannerDesktop"
				title="Desktop banner (required)"
				no-image-text="No banner"
				show-helper
				width="550"
				height="550"
				class="mt-4"
				required
			/>

			<BaseMediaSelectorBox
				ref="bannerMobile"
				v-model="bannerMobile"
				title="Mobile banner (required)"
				no-image-text="No banner"
				show-helper
				width="362"
				height="362"
				class="mt-4"
				required
			/>
		</BannerContentWrapper>

		<BaseActionPanelStickyFooter
			:disabled-confirm="isLoading || isUpdating"
			:is-edit="isEditMode"
			:remove-text="isEditMode ? 'Remove banner' : null"
			@onConfirm="handleSubmit"
			@onCancel="$router.push({ name: 'BannerHighlightLobList'})"
			@onRemove="isEditMode ? $refs['modal-remove'].open() : null"
		/>

		<BaseModalConfirmDelete
			ref="modal-remove"
			:handle-remove="deleteBanner.bind(null, id)"
			title="Remove this banner?"
			description="By removing this, all banner info will be disappeared from the list and linked widgets."
			@onSuccess="$router.push({ name: 'BannerHighlightLobList'})"
		/>
	</form>
</template>

<script>
import { mapActions } from 'vuex';

import BannerGeneral from '@/components/BannerGeneral.vue';
import BannerDate from '@/components/BannerDate.vue';
import BannerContentWrapper from '@/components/BannerContentWrapper.vue';
import BaseMediaSelectorBox from '@/components/BaseMediaSelectorBox.vue';

import { BANNER_TYPE, BANNER_IMAGE_TYPE } from '../enums/banners';
import { WIDGET_TYPE } from '../enums/widgets';
import { convertDateTimeToUTC, priceToAPI } from '../assets/js/helpers';

export default {
	name: 'BannerHighlightLobForm',

	components: {
		BannerGeneral,
		BannerDate,
		BannerContentWrapper,
		BaseMediaSelectorBox,
	},

	props: {
		isEditMode: {
			type: Boolean,
			default: false,
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		isUpdating: {
			type: Boolean,
			default: false,
		},
		data: {
			type: Object,
			default: null,
		},
		bannerType: {
			type: String,
			default: BANNER_TYPE.highlight_lob,
		},
	},

	data() {
		return {
			id: null,
			name: '',
			priority: 0,
			pageWidgets: [],
			status: true,
			startDate: null,
			startTime: '00:00',
			endDate: null,
			endTime: '23:59',
			bannerDesktop: null,
			bannerMobile: null,
			bannerTitleEn: '',
			bannerTitleTh: '',
			bannerDescriptionEn: '',
			bannerDescriptionTh: '',
			bannerLowestPrice: null,
			bannerButtonTextEn: '',
			bannerButtonTextTh: '',
			bannerLink: '',
			bannerLabels: [],
		};
	},

	computed: {
		widgetType() {
			return WIDGET_TYPE[this.bannerType];
		},
	},

	watch: {
		data: {
			immediate: true,
			handler(value) {
				if (this.isEditMode && !this.isUpdating && value) {
					this.id = value.id;
					this.name = value.name;
					this.priority = value.priority;
					this.status = value.status;
					this.startDate = value.startDate;
					this.startTime = value.startTime;
					this.endDate = value.endDate;
					this.endTime = value.endTime;

					if (value.banners && value.banners.lobBannerDesktop) {
						this.bannerDesktop = value.banners.lobBannerDesktop;
						this.bannerTitleEn = value.banners.lobBannerDesktop.titleEn || '';
						this.bannerTitleTh = value.banners.lobBannerDesktop.titleTh || '';
						this.bannerDescriptionEn = value.banners.lobBannerDesktop.descriptionEn || '';
						this.bannerDescriptionTh = value.banners.lobBannerDesktop.descriptionTh || '';
						this.bannerLowestPrice = value.banners.lobBannerDesktop.lowestPrice || null;
						this.bannerButtonTextEn = value.banners.lobBannerDesktop.buttonTextEn || '';
						this.bannerButtonTextTh = value.banners.lobBannerDesktop.buttonTextTh || '';
					}

					if (value.banners && value.banners.lobBannerMobile) {
						this.bannerMobile = value.banners.lobBannerMobile;
					}

					this.bannerLabels = value.banners?.lobBannerLabels || [];
					this.bannerLink = value.banners?.bannerLink || '';
					this.pageWidgets = value.pageWidgets || [];
				}
			},
		},
	},

	methods: {
		...mapActions({
			deleteBanner: 'banners/deleteBanner',
		}),

		handleChangeEndDate(value) {
			this.endDate = value;
		},

		addBanner(file, type, link, { titleTh, titleEn, descriptionTh, descriptionEn, lowestPrice, buttonTextTh, buttonTextEn, labelIds }) {
			const result = {};
			result.file = file ? file.id : null;
			result.type = type;

			if (link) {
				result.link = link;
			}

			result.title_th = titleTh;
			result.title_en = titleEn;
			result.description_th = descriptionTh;
			result.description_en = descriptionEn;
			result.lowest_price = lowestPrice ? priceToAPI(Number(lowestPrice)) : null;
			result.button_text_th = buttonTextTh;
			result.button_text_en = buttonTextEn;
			result.label_ids = labelIds;

			return result;
		},

		async handleSubmit() {
			const generalRef = this.$refs.general.$v;
			const dateRef = this.$refs.date.$v;

			const contentLobRef = this.$refs.contentLob.$v;
			const bannerDesktopRef = this.$refs.bannerDesktop.$v;
			const bannerMobileRef = this.$refs.bannerMobile.$v;

			generalRef.$touch();
			dateRef.$touch();

			contentLobRef.$touch();
			bannerDesktopRef.$touch();
			bannerMobileRef.$touch();

			if (
				!generalRef.$invalid &&
				!dateRef.$invalid &&
				!contentLobRef.$invalid &&
				!bannerDesktopRef.$invalid &&
				!bannerMobileRef.$invalid
			) {
				const bannerContent = {
					titleTh: this.bannerTitleTh,
					titleEn: this.bannerTitleEn,
					descriptionTh: this.bannerDescriptionTh,
					descriptionEn: this.bannerDescriptionEn,
					lowestPrice: this.bannerLowestPrice,
					buttonTextTh: this.bannerButtonTextTh,
					buttonTextEn: this.bannerButtonTextEn,
					labelIds: this.bannerLabels.map((item) => item.id),
				};

				const data = {
					name: this.name,
					type: this.bannerType,
					priority: Number(this.priority, 10),
					is_visible: this.status,
					start_at: convertDateTimeToUTC(this.startDate, this.startTime),
					end_at: convertDateTimeToUTC(this.endDate, this.endTime),
					banners: [
						this.addBanner(
							this.bannerDesktop,
							BANNER_IMAGE_TYPE.homepage_highlight_1_banner_image,
							this.bannerLink,
							bannerContent,
						),
						this.addBanner(
							this.bannerMobile,
							BANNER_IMAGE_TYPE.homepage_highlight_1_mobile_banner_image,
							this.bannerLink,
							bannerContent,
						),
					],
					page_widget_ids: this.pageWidgets.map((widget) => widget.id),
				};

				this.$emit('onSubmit', data);
			}
		},

		handleRemove() {
			this.$emit('onRemove');
		},
	},
};
</script>

<style lang="scss" scoped>
	.remove-banners {
		font-size: rem(14);
		color: $color-orange;

		align-items: center;

		cursor: pointer;
	}
</style>
